<template>
  <DialogFull :data="dialogSettings" @close="close">
    <template v-slot:dialog.body>
      <div class="container col-lg-6 col-sm-12 offers-form">
        <h1>New Offer</h1>
        <div class="d-flex align-center mt-4">
          <v-avatar tile v-if="!isMobile">
            <div>
              <svg
                width="25"
                height="25"
                viewBox="0 0 20 20"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  class="fill"
                  d="M12 4.21053V2.10526H8V4.21053H12ZM2 6.31579V17.8947H18V6.31579H2ZM18 4.21053C19.11 4.21053 20 5.14737 20 6.31579V17.8947C20 19.0632 19.11 20 18 20H2C0.89 20 0 19.0632 0 17.8947L0.00999999 6.31579C0.00999999 5.14737 0.89 4.21053 2 4.21053H6V2.10526C6 0.936842 6.89 0 8 0H12C13.11 0 14 0.936842 14 2.10526V4.21053H18Z"
                />
              </svg>
            </div>
          </v-avatar>
          <div class="d-flex ml-2">
            <strong class="mr-1">{{ currentBooking.title }}</strong>
            | {{ organisation }}
          </div>
        </div>
        <ValidationObserver ref="createOffer">
          <ValidationProvider
            v-slot="{ errors }"
            name="candidate"
            rules="required"
          >
            <Select
              v-model="applicationId"
              autocomplete
              height="50"
              class="mt-4"
              label="Candidate"
              placeholder="Select Candidate"
              item-text="fullName"
              item-value="applicationId"
              :loading="isLoadingBookingCandidates"
              :items="formattedCandidates"
              :error-messages="getErrorMessages(errors)"
              :disabled="isLoadingBookingCandidates"
            />
          </ValidationProvider>
          <ValidationProvider
            v-slot="{ errors }"
            name="salary"
            rules="required|minNumber:1"
          >
            <v-text-field
              v-model="salary"
              class="offers-label"
              label="Salary"
              prepend-inner-icon="mdi-currency-gbp"
              type="number"
              :error-messages="getErrorMessages(errors)"
            />
          </ValidationProvider>
          <DatePicker
            label="Proposed Start Date"
            :showValidationErrors="showValidationErrors"
            @onChange="setStartDate"
            required
          />
        </ValidationObserver>
      </div>
    </template>
    <template v-slot:dialog.action>
      <PrimaryButton @click.native="handleCreateOffer" :loading="creating">
        Create Offer
      </PrimaryButton>
    </template>
  </DialogFull>
</template>

<script>
import DialogFull from "@/components/common/DialogFull";
import Select from "@/components/common/Select";
import DatePicker from "@/components/common/DatePicker";
import PrimaryButton from "@/components/common/Button/PrimaryButton";
import {
  BOOKINGS_NAMESPACE,
  FETCH_CURRENT_BOOKING_CANDIDATES
} from "@/store/modules/bookings/actions";
import { createNamespacedHelpers } from "vuex";
import {
  GET_CURRENT_BOOKING_CANDIDATES,
  IS_LOADING_BOOKING_CANDIDATES
} from "@/store/modules/bookings/getters";
import { getCandidateName } from "@/utils/users";
import { map } from "lodash";
import { CREATE_OFFER } from "@/store/modules/offers/actions";
import { OFFERS_NAMESPACE } from "@/store/modules/offers";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import { formatDate } from "@/utils/time";
import { getAmountInApiFormat } from "@/utils/helpers";

const { mapActions, mapGetters } = createNamespacedHelpers(BOOKINGS_NAMESPACE);
const { mapActions: offerMapActions } = createNamespacedHelpers(
  OFFERS_NAMESPACE
);

export default {
  props: {
    isOpen: Boolean,
    currentBooking: { type: Object, required: true }
  },
  components: {
    DialogFull,
    Select,
    DatePicker,
    PrimaryButton,
    ValidationObserver,
    ValidationProvider
  },
  async created() {
    const applicationsRequestParams = {
      filter: {
        valid_candidate: true,
        status: ["evaluated", "shortlisted"]
      }
    };
    await this.fetchCurrentBookingApplications({
      bookingId: this.currentBooking.id,
      params: applicationsRequestParams
    });
  },
  data() {
    return {
      creating: false,
      showValidationErrors: false,
      applicationId: null,
      salary: null,
      startDate: null
    };
  },
  computed: {
    ...mapGetters({
      allApplications: GET_CURRENT_BOOKING_CANDIDATES,
      isLoadingBookingCandidates: IS_LOADING_BOOKING_CANDIDATES
    }),
    dialogSettings() {
      return {
        dialog: this.isOpen,
        title: "New Offer"
      };
    },
    organisation() {
      return this.currentBooking.client.name;
    },
    formattedCandidates() {
      return map(this.allApplications, ({ user, id: applicationId }) => ({
        applicationId,
        ...user,
        fullName: getCandidateName({ user, id: applicationId })
      }));
    }
  },
  methods: {
    ...mapActions({
      fetchCurrentBookingApplications: FETCH_CURRENT_BOOKING_CANDIDATES
    }),
    ...offerMapActions({
      createOffer: CREATE_OFFER
    }),
    async handleCreateOffer() {
      this.showValidationErrors = true;
      const isValid = await this.$refs.createOffer.validate();
      if (isValid) {
        this.creating = true;
        const {
          data: { id: newOfferId }
        } = await this.createOffer({
          salary: getAmountInApiFormat(this.salary),
          application: { id: this.applicationId },
          startDate: this.startDate,
          status: "live"
        });
        if (newOfferId) {
          await this.$router.push(`/offers/${newOfferId}`);
        }
        this.close();
        this.creating = false;
        this.showValidationErrors = false;
      }
    },
    setStartDate(date) {
      this.startDate = formatDate({ date });
    },
    close() {
      this.$emit("close");
    },
    getErrorMessages(errors) {
      return this.showValidationErrors ? errors : "";
    }
  }
};
</script>

<style lang="scss" scope>
.offers-form {
  .v-avatar {
    background-color: #e3f2ff !important;
    border-radius: 4px;
  }

  .offers-label {
    label {
      left: -36px !important;
    }
  }
}
</style>
